import React from 'react';
import { Constants } from '@pwc/pwc-deals-web-common';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TextInput, RadioGroup, Loader, Icon } from '@pwc/pwc-deals-ui';
import FilterDropdown from '../../../../../common/components/FilterDropdown/FilterDropdown';
import TextAreaFieldset from '../../../../../common/components/TextAreaFieldset/TextAreaFieldset';
import PublishButton from '../../../../../common/components/PublishButton';
import { RadioGroupWithSubtitle } from '../../../common/RadioGroupWithSubtitle';
import Form from '../../common/components/Form';
import CancelButton from '../../common/components/CancelButtonLink';
import FormBottomActionsContainer from '../../common/components/FormBottomActionsContainer';
import {
  dealOptions,
  clientTargetoptions,
  consentLevelOptionsWithLinks,
} from '../../../common/projectOptions';
import {
  getIsConfirmButtonClicked,
  getProjectInitConfig,
} from '../../../projectInitSlice';
import SelectMultiple from '../../../../../common/components/SelectMultiple/SelectMultiple';
import { useProjectOptionsFromApi } from '../../../../../common/hooks/useProjectOptionsFromApi';
import './MetaDataStepDisplay.scss';
import {
  defaultTerritory,
  isEUTerritory,
  isUSTerritory,
} from '../../../../../common/helpers/projectInitUtil';

const MetaDataStepDisplay = ({
  formValues,
  formTouches,
  formErrors,
  isValid,
  setIsValid,
  onChange,
  onBlur,
  onCancel,
  onNextClick,
  isSaving,
}) => {
  const { t } = useTranslation();
  const { territoryCode } = useSelector(getProjectInitConfig);
  const { isConfirmButtonClicked } = useSelector(getIsConfirmButtonClicked);
  const { isLoading, industryOptions, serviceOptions } =
    useProjectOptionsFromApi(territoryCode);

  if (isLoading) {
    return <Loader data-testid="project-options-loading" />;
  }
  const changeDefaultTerritoryInRefresh = (territoryName) => {
    if (
      territoryName === defaultTerritory.territoryName &&
      isUSTerritory &&
      !isConfirmButtonClicked
    ) {
      return Constants.TERRITORIES.BRAZIL;
    }
    if (
      territoryName === defaultTerritory.territoryName &&
      isEUTerritory &&
      !isConfirmButtonClicked
    ) {
      return Constants.TERRITORIES.AUSTRIA;
    }
    if (
      territoryName === defaultTerritory.territoryName &&
      defaultTerritory.territoryCode === Constants.TERRITORY_CODES.AUSTRALIA &&
      !isConfirmButtonClicked
    ) {
      return Constants.TERRITORIES.INDIA;
    }
    return territoryName;
  };
  return (
    <Form onSubmit={onNextClick} data-testid="project-create-form">
      <div className="step-1-form">
        <TextInput
          label={t('territory')}
          field={{
            name: 'territory',
            value: changeDefaultTerritoryInRefresh(formValues.territory),
            onChange: () => {},
          }}
          error=""
          basic
          disabled
        />
        <h3>{t('genInformation')}</h3>
        <TextInput
          basic
          label={t('projectName')}
          field={{
            name: 'projectName',
            value: formValues.projectName,
            onChange,
          }}
          error={formTouches.projectName ? formErrors.projectName : ''}
          onBlur={onBlur('projectName')}
          helperText={t('includeProjectMessage')}
        />
        <TextAreaFieldset
          basic
          label={t('descOptional')}
          field={{
            name: 'description',
            value: formValues.description,
            onChange,
          }}
          error=""
          onBlur={onBlur('description')}
        />
        <h3>{t('dealInformation')}</h3>
        <RadioGroup
          label={t('dealType')}
          field={{
            name: 'dealType',
            value: formValues.dealType,
            onChange,
          }}
          options={dealOptions}
          direction="column"
          onBlur={onBlur('dealType')}
          error={formTouches.dealType ? formErrors.dealType : ''}
        />
        <div className="services-container">
          <SelectMultiple
            matchOnStartsWith={false}
            minimumSearchStringLength={2}
            fieldName="services"
            label={t('servicesProducts')}
            placeholder={t('searchService')}
            options={serviceOptions}
            selections={formValues.services}
            onChange={(selections) =>
              onChange({ target: { name: 'services', value: selections } })
            }
            onBlur={onBlur('services')}
            error={formTouches.services ? formErrors.services : ''}
            noMatchElement={null}
          />
        </div>
        <h3>{t('clientDetails')}</h3>
        <TextInput
          basic
          label={t('clientName')}
          field={{
            name: 'clientName',
            value: formValues.clientName,
            onChange,
          }}
          error={formTouches.clientName ? formErrors.clientName : ''}
          onBlur={onBlur('clientName')}
        />
        <FilterDropdown
          name="industryId"
          label={t('IndustrySubsector')}
          options={industryOptions}
          placeholder={t('selectIndustry')}
          selectedId={formValues.industryId || null}
          setIsValid={setIsValid}
          onChange={(selectedId) =>
            onChange({
              target: { name: 'industryId', value: selectedId },
            })
          }
        />
        <br />
        <RadioGroupWithSubtitle
          label={t('DataConcent')}
          subTitle={
            <>
              {t('concentMessage')}{' '}
              <a
                target="_blank"
                className="external-source-link hyperlink"
                href="https://sites.google.com/pwc.com/workbenchpwc/help-and-support/risk-and-legal?authuser=0#h.ij1gipr1r4r6"
                rel="noreferrer"
              >
                {t('readBusiness')}
              </a>{' '}
              <Icon
                name="box-arrow-up-right"
                className="fill icon-orange"
                width="16"
              />
            </>
          }
          field={{
            name: 'consentLevel',
            value: formValues.consentLevel,
            onChange,
          }}
          options={consentLevelOptionsWithLinks}
        />
        <br />
        <RadioGroupWithSubtitle
          field={{
            name: 'isClientTarget',
            value: formValues.isClientTarget,
            onChange,
          }}
          options={clientTargetoptions}
          label={t('isCLientTarget')}
          subTitle={t('subjectWork')}
        />
        {!JSON.parse(formValues.isClientTarget) && (
          <>
            <h3>{t('targetDetails')}</h3>
            <TextInput
              basic
              label={t('targetOptional')}
              field={{
                name: 'target',
                value: formValues.target,
                onChange,
              }}
              error=""
              onBlur={onBlur('target')}
            />
            <FilterDropdown
              name="targetIndustryId"
              label={t('IndustrySubSector')}
              options={industryOptions}
              placeholder={t('selectIndustryKey')}
              selectedId={formValues.targetIndustryId || null}
              setIsValid={setIsValid}
              onChange={(selectedId) =>
                onChange({
                  target: { name: 'targetIndustryId', value: selectedId },
                })
              }
            />
          </>
        )}
      </div>
      <br />
      <div className="separator" />
      <FormBottomActionsContainer>
        <CancelButton onClick={onCancel} />
        <PublishButton
          isSaving={isSaving}
          isValid={isValid}
          buttonSize="sm"
          onSaveText={t('saving')}
          standbyText={t('next')}
          buttonPosition="project-details"
        />
      </FormBottomActionsContainer>
    </Form>
  );
};

export default MetaDataStepDisplay;
